import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import './CollapsiblePanel.css';

interface CollapsiblePanelProps {
    title: string;
    children: React.ReactNode;
    defaultCollapsed?: boolean;
    className?: string;
    /** Unique identifier for this panel - used for persistence. If not provided, state won't be persisted */
    panelId?: string;
}

const STORAGE_KEY_PREFIX = 'panel-state-';

export const CollapsiblePanel: React.FC<CollapsiblePanelProps> = ({
    title,
    children,
    defaultCollapsed = false,
    className = '',
    panelId
}) => {
    const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
    const [isInitialized, setIsInitialized] = useState(false);

    // Load initial state from localStorage on mount
    useEffect(() => {
        if (panelId && typeof window !== 'undefined') {
            const stored = localStorage.getItem(STORAGE_KEY_PREFIX + panelId);
            if (stored !== null) {
                setIsCollapsed(stored === 'true');
            }
            setIsInitialized(true);
        } else {
            setIsInitialized(true);
        }
    }, [panelId]);

    // Persist state changes to localStorage (only after initialization and if panelId is provided)
    useEffect(() => {
        if (isInitialized && panelId && typeof window !== 'undefined') {
            localStorage.setItem(STORAGE_KEY_PREFIX + panelId, String(isCollapsed));
        }
    }, [panelId, isCollapsed, isInitialized]);

    const toggleCollapsed = () => {
        setIsCollapsed(prev => !prev);
    };

    return (
        <div className={`collapsible-panel ${className}`}>
            <div
                className="collapsible-panel-header"
                onClick={toggleCollapsed}
            >
                <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: 0, cursor: 'pointer' }}>
                    <FontAwesomeIcon icon={isCollapsed ? faChevronRight : faChevronDown} style={{ color: 'var(--accent-color)', width: '14px' }} />
                    {title}
                </h3>
            </div>

            {!isCollapsed && (
                <div className="collapsible-panel-content">
                    {children}
                </div>
            )}
        </div>
    );
};
